<template>
  <div>
    <section class="page-section page-section--expanded profit-calculator-section">
      <header class="page-section-header">
        <h2 class="page-section-header__title">
          {{ $t('calculator.profit-calculator.title', {
            cryptoCode: selectedCryptoCurrency.code,
            cryptoName: selectedCryptoCurrency.name,
          }) }}
        </h2>
        <p class="page-section-header__subtitle">
          {{ $t('calculator.profit-calculator.subtitle', {
            cryptoCode: selectedCryptoCurrency.code,
            cryptoName: selectedCryptoCurrency.name,
          }) }}
        </p>
      </header>
      <div class="profit-calculator">
        <div class="profit-calculator-form">
          <div class="profit-calculator-form__field-group">
            <div class="profit-calculator-form__label">
              <img src="@kanoAssets/images/icons/chart.svg?url" loading="lazy" width="18" height="18" :alt="$t('calculator.profit-trend-form.investment-details')" />
              {{ $t('calculator.profit-trend-form.investment-details') }}
            </div>
            <CryptoDropDownInput v-model="selectedCryptoCurrency" />
            <FiatDropDownInput v-model="selectedFiatCurrency">
              <div class="amount-input" @click.stop>
                <label class="amount-input__label" for="profit-amount-input">
                  {{ $t('calculator.profit-trend-form.amount') }}
                </label>
                <AmountInput v-model="amount" />
              </div>
            </FiatDropDownInput>
          </div>
          <div class="profit-calculator-form__field-group">
            <div class="profit-calculator-form__label">
              <img src="@kanoAssets/images/icons/calendar.svg?url" loading="lazy" width="18" height="18" :alt="$t('calculator.profit-trend-form.start-date')" />
              {{ $t('calculator.profit-trend-form.start-date') }}
            </div>
            <DateSelect v-model="startDate" :startFrom="historyStartDate" />
          </div>
          <div class="profit-calculator-form__field-group">
            <div class="profit-calculator-form__label">
              <img src="@kanoAssets/images/icons/calendar.svg?url" loading="lazy" width="18" height="18" :alt="$t('calculator.profit-trend-form.end-date')" />
              {{ $t('calculator.profit-trend-form.end-date') }}
            </div>
            <DateSelect v-model="endDate" :startFrom="startDate" />
          </div>

          <div class="form-footer">
            <div class="form-footer__disclaimer">
              *{{ $t('calculator.profit-trend-form.disclaimer') }}
            </div>
            <button class="button button--secondary" @click="onCalculateProfitClick">
              {{ $t('calculator.profit-trend-form.btn') }}
            </button>
          </div>
        </div>
        <div class="profit-calculator-chart">
          <div v-if="!dataset.length" class="profit-calculator-chart__loader">
            <ExchangeLoading />
          </div>
          <ProfitChart v-else :chartData="dataset" :fiatCurrency="selectedFiatCurrency">
            <template #profit>
              <div class="profit-amount">
                {{ formatAmount(profit, selectedFiatCurrency.code) }}
              </div>
              <ProfitChange :startAmount="currentChartState.amount" :endAmount="profit" />
            </template>
          </ProfitChart>
        </div>
      </div>
      <div v-if="!dataset.length" class="profit-results loading">
        <div class="profit-results__description loading__item"></div>
        <div class="profit-results__list loading__item"></div>
        <div class="profit-results__cta loading__item"></div>
      </div>
      <ProfitResults
        v-else
        :fiatCurrency="selectedFiatCurrency"
        :cryptoCurrency="selectedCryptoCurrency"
        :startData="startData"
        :endData="endData"
      />
    </section>
    <section v-if="!dataset.length" class="page-section page-section--expanded investment-growth loading">
      <div class="investment-growth__title loading__item"></div>
      <div class="investment-growth__list loading__item"></div>
      <div class="investment-growth__cta loading__item"></div>
    </section>
    <InvestmentGrowth
      v-else
      :investmentData="investmentData"
      :fiatCurrency="selectedFiatCurrency"
      :cryptoCurrency="selectedCryptoCurrency"
      :startAmount="amount"
    />
  </div>
</template>

<script setup lang="ts">
import { ProfitCalculatorChartData, ProfitCalculatorCurrency } from "@kano/types/profit-calculator";
import CryptoDropDownInput from "@kano/components/ProfitCalculator/Form/CryptoDropDownInput.vue";
import { computed, onMounted, ref } from "vue";
import { pushToDataLayer, DataLayerEvent } from "@paybis/frontend-common-lib/src/services/gtm";
import FiatDropDownInput from "@kano/components/ProfitCalculator/Form/FiatDropDownInput.vue";
import AmountInput from "@kano/components/ProfitCalculator/Form/AmountInput.vue";
import DateSelect from "@kano/components/ProfitCalculator/Form/DateSelect.vue";
import PricesClient from "@kano/services/clients/seo-client";
import { predictCryptoPriceTrend } from "@kano/services/CryptoPricePredictionService";
import ProfitChart from "@kano/components/ProfitCalculator/ProfitChart.vue";
import { i18n } from "@kano/i18n";
import { formatNumberStandard, CurrencyFormat } from "@kano/helpers/numberFormatter";
import ProfitChange from "@kano/components/ProfitCalculator/ProfitChange.vue";
import ProfitResults from "@kano/components/ProfitCalculator/ProfitResults.vue";
import ExchangeLoading from "@kano/components/ExchangeFormV2/ExchangeLoading.vue";
import InvestmentGrowth from "@kano/components/ProfitCalculator/InvestmentGrowth.vue";

const props = withDefaults(defineProps<{
  cryptoCurrency: ProfitCalculatorCurrency,
  fiatCurrency: ProfitCalculatorCurrency,
  amount: number,
  startDate: Date,
  endDate: Date,
}>(), {
  amount: 100,
  startDate: new Date(new Date().setFullYear(new Date().getFullYear() - 1)),
  endDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)),
});

const amount = ref<float>(props.amount);
const selectedCryptoCurrency = ref<ProfitCalculatorCurrency>(props.cryptoCurrency);
const selectedFiatCurrency = ref<ProfitCalculatorCurrency>(props.fiatCurrency);
const startDate = ref<number>(props.startDate);
const endDate = ref<number>(props.endDate);
const dataset = ref<Array<ProfitCalculatorChartData>>([]);

type ChartState = {
  cryptoCode: string|null;
  fiatCode: string|null;
  amount: number;
  startDate: Date;
  endDate: Date;
}
const currentChartState = ref<ChartState>({
  cryptoCode: null,
  fiatCode: null,
  amount: amount.value,
  startDate: startDate.value,
  endDate: endDate.value,
});

const priceHistory = ref([]);
const historyStartDate = ref<Date>((new Date(new Date().setFullYear(new Date().getFullYear() - 3))));
const getPriceHistory = async (cryptoCode, fiatCode) => {
  if (currentChartState.value.cryptoCode !== cryptoCode || currentChartState.value.fiatCode !== fiatCode) {
    priceHistory.value = await PricesClient.getHistoricData('CCCAGG', selectedCryptoCurrency.value.code, selectedFiatCurrency.value.code, 'three_years');
  }

  return priceHistory.value;
}

const startData = computed(() => {
  if (!dataset.value?.length) {
    return 0;
  }
  return dataset.value[0];
})

const endData = computed(() => {
  if (!dataset.value?.length) {
    return 0;
  }
  return dataset.value.at(-1);
})

const profit = computed(() => {
  if (!endData.value) {
    return 0;
  }
  const { predictedValue } = endData.value;

  return predictedValue;
});

const isTrendParametersChanged = () => {
  if (currentChartState.value.cryptoCode !== selectedCryptoCurrency.value.code) {
    return true;
  }
  if (currentChartState.value.fiatCode !== selectedFiatCurrency.value.code) {
    return true;
  }
  if (currentChartState.value.startDate !== startDate.value) {
    return true;
  }
  if (currentChartState.value.endDate !== endDate.value) {
    return true;
  }

  return currentChartState.value.amount !== amount.value;
}
const onCalculateProfitClick = () => {
  pushToDataLayer({ event: DataLayerEvent.CALCULATE_PROFIT_CLICK });
  calculateProfit();
};

const calculateProfit = async () => {
  if (!isTrendParametersChanged()) {
    return;
  }

  const historicalPrices = await getPriceHistory(selectedCryptoCurrency.value.code, selectedFiatCurrency.value.code);
  dataset.value = predictCryptoPriceTrend(amount.value, startDate.value, endDate.value, historicalPrices);

  currentChartState.value = {
    amount: amount.value,
    cryptoCode: selectedCryptoCurrency.value.code,
    fiatCode: selectedFiatCurrency.value.code,
    startDate: startDate.value,
    endDate: endDate.value,
  };
}

const investmentData = computed(() => {
  if (!dataset.value?.length) {
    return  [];
  }

  // We need to display 10 data points from the dataset, evenly spaced.
  const count = 10;
  const startIndex = Math.floor(dataset.value.length / count);
  const actualStep = (dataset.value.length - 1 - startIndex) / (count - 1);
  const result = [];

  for (let i = 0; i < count; i++) {
    const index = Math.round(startIndex + i * actualStep);
    const item = dataset.value[index];
    const prevItem = dataset.value[Math.round(index - actualStep)];

    result.push({
      date: new Date(item.date),
      price: item.predictedPrice,
      balance: item.predictedValue,
      profitDiff: item.predictedValue - prevItem.predictedValue,
    });
  }

  // Final investment profit needs to be at the top of the list
  return result.sort((a, b) => b.date - a.date);
})

const formatAmount = (amount: number, currencyCode: string) => {
  return formatNumberStandard(amount, i18n.locale, currencyCode, CurrencyFormat.symbol);
}

onMounted(() => {
  calculateProfit();
});
</script>

<style scoped lang="scss">
@use '@kanoAssets/styles/breakpoints.scss';

.profit-calculator-form {
  &__field-group {
    padding-bottom: 24px;
    display: flex;
    flex-direction: column;
    gap: 12px;

    @include breakpoints.desktop {
      padding-bottom: 32px;
      gap: 16px;
    }
  }

  &__label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-weight: 600;
  }
}

.amount-input {
  display: flex;
  flex-direction: column;

  &__label {
    font-size: 14px;
    font-weight: normal;
    color: var(--color-base-64);
    line-height: 20px;
  }

  &__value {
    width: 100%;
    min-width: 0;
    font-size: 16px;
    line-height: 24px;

    &:focus {
      outline: none;
    }

    &::-webkit-contacts-auto-fill-button {
      visibility: hidden;
    }

    @include breakpoints.desktop {
      font-size: 18px;
      line-height: 26px;
    }
  }
}

.form-footer {
  display: flex;
  flex-direction: column;
  align-items: center;

  &__disclaimer {
    font-size: 14px;
    line-height: 20px;
    color: var(--color-base-64);
    padding-bottom: 32px;
  }
}

.profit-amount {
  font-weight: 700;
  font-size: 32px;
  line-height: 100%;

  @include breakpoints.desktop {
    font-size: 48px;
    line-height: 58px;
  }
}

.profit-calculator-chart {
  padding: 24px;

  &__loader {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 429px;
  }
}
</style>
